import { useState } from "react";
import * as React from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CategorySelector } from "./CategorySelector";
import { TaskDetailsForm } from "./TaskDetailsForm";
import { CheckpointsEditor } from "./CheckpointsEditor";
import { TaskCategoryType } from "@/lib/taskCategoryConfig";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { getPhaseTemplateByCategory, categoryHasPhases, getTemplateTotalDuration } from "@/lib/phaseTemplates";
import { CheckCircle2, Clock, Layers } from "lucide-react";

interface TaskCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (taskData: any) => void;
  taskCounts?: Record<string, number>;
  editTask?: any;
}

export interface Checkpoint {
  id: string;
  title: string;
  completed: boolean;
  due_date?: string;
  priority?: string;
  notes?: string;
  order: number;
}

export interface TaskFormData {
  category_type: TaskCategoryType | null;
  custom_category?: string;
  task_title: string;
  task_description: string;
  importance: string | null;
  urgency: string | null;
  due_date?: Date;
  assignees: string[];
  assignedMemberIds?: string[]; // New: UNA member IDs for proper member-based assignment
  tags: string[];
  checkpoints: Checkpoint[];
  estimated_hours?: number;
  privacy?: string;
  related_items?: any;
  recurring_config?: any;
  notifications?: any;
  customFields?: Record<string, any>; // Custom fields for category-specific data
  usePhases?: 'standard' | 'skip' | 'custom' | null; // Phase configuration
}

export const TaskCreationModal = ({ isOpen, onClose, onSave, taskCounts, editTask }: TaskCreationModalProps) => {
  const [step, setStep] = useState(1);
  const [showPhaseConfig, setShowPhaseConfig] = useState(false);
  const isEditingRef = React.useRef(false);
  const [formData, setFormData] = useState<TaskFormData>({
    category_type: null,
    task_title: "",
    task_description: "",
    importance: null,
    urgency: null,
    assignees: [],
    tags: [],
    checkpoints: [],
    privacy: "all_members",
    related_items: {},
    recurring_config: {},
    notifications: {},
    usePhases: null
  });

  // Load edit task data when provided - FIXED race condition
  React.useEffect(() => {
    if (!isOpen) {
      // Reset when closing and clear edit flag
      isEditingRef.current = false;
      setFormData({
        category_type: null,
        task_title: "",
        task_description: "",
        importance: null,
        urgency: null,
        assignees: [],
        tags: [],
        checkpoints: [],
        privacy: "all_members",
        related_items: {},
        recurring_config: {},
        notifications: {}
      });
      setStep(1);
      return;
    }

    if (editTask) {
      // Mark as editing to prevent reset
      isEditingRef.current = true;
      console.log('[TaskCreationModal] Loading edit task data:', editTask);
      
      // Extract assignee IDs from the assignees array (which may be objects or strings)
      const assignedMemberIds = Array.isArray(editTask.assignees) 
        ? editTask.assignees
            .filter((a: any) => a && typeof a === 'object' && a.id)
            .map((a: any) => a.id)
        : [];
      
      // Extract legacy string assignees for backward compatibility
      const legacyAssignees = Array.isArray(editTask.assignees)
        ? editTask.assignees
            .filter((a: any) => typeof a === 'string')
        : [];

      const populatedData = {
        category_type: editTask.category_type,
        custom_category: editTask.custom_category,
        task_title: editTask.task_title || "",
        task_description: editTask.task_description || "",
        importance: editTask.importance || null,
        urgency: editTask.urgency || null,
        due_date: editTask.due_date ? new Date(editTask.due_date) : undefined,
        assignees: legacyAssignees,
        assignedMemberIds: assignedMemberIds.length > 0 ? assignedMemberIds : undefined,
        tags: Array.isArray(editTask.tags) ? editTask.tags : [],
        checkpoints: Array.isArray(editTask.checkpoints) ? editTask.checkpoints : [],
        estimated_hours: editTask.estimated_hours || undefined,
        privacy: editTask.privacy || "all_members",
        related_items: editTask.related_items || {},
        recurring_config: editTask.recurring_config || {},
        notifications: editTask.notifications || {},
        customFields: editTask.customFields || {}
      };
      
      console.log('[TaskCreationModal] Populated form data:', populatedData);
      setFormData(populatedData);
      setStep(2); // Skip category selection for edits
    } else if (isOpen && !isEditingRef.current) {
      // ONLY reset for new task creation if not in edit mode
      setFormData({
        category_type: null,
        task_title: "",
        task_description: "",
        importance: null,
        urgency: null,
        assignees: [],
        tags: [],
        checkpoints: [],
        privacy: "all_members",
        related_items: {},
        recurring_config: {},
        notifications: {}
      });
      setStep(1);
    }
  }, [editTask, isOpen]);

  const handleCategorySelect = (category: TaskCategoryType) => {
    setFormData({ ...formData, category_type: category });
    setStep(2);
  };

  const handleDetailsUpdate = (updates: Partial<TaskFormData>) => {
    setFormData({ ...formData, ...updates });
  };

  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSave = async () => {
    if (editTask) {
      // Update existing task
      try {
        const { error } = await supabase
          .from('custom_tasks')
          .update({
            category_type: formData.category_type,
            custom_category: formData.custom_category || null,
            task_title: formData.task_title,
            task_description: formData.task_description || null,
            importance: formData.importance,
            urgency: formData.urgency,
            due_date: formData.due_date?.toISOString().split('T')[0] || null,
            checkpoints: formData.checkpoints as any || [],
            assignees: formData.assignees as any || [],
            tags: formData.tags as any || [],
            estimated_hours: formData.estimated_hours || null,
            privacy: formData.privacy || "all_members",
            related_items: formData.related_items as any || {},
            recurring_config: formData.recurring_config as any || {}
          })
          .eq('id', editTask.id);

        if (error) throw error;
      } catch (error) {
        console.error('Error updating task:', error);
      }
    } else {
      // Create new task
      onSave(formData);
    }
    handleClose();
  };

  const handleClose = () => {
    // Don't reset here - let the useEffect handle it
    onClose();
  };

  const canProceed = () => {
    if (step === 1) return formData.category_type !== null;
    if (step === 2) return formData.task_title.trim().length > 0;
    return true;
  };

  const totalSteps = editTask ? 0 : 3;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] md:w-full p-4 md:p-6">
        <DialogHeader>
          <DialogTitle className="text-lg md:text-xl">
            {editTask ? "Edit Task" : (
              <>
                {step === 1 && "Select Task Category"}
                {step === 2 && "Task Details"}
                {step === 3 && "Add Checkpoints (Optional)"}
              </>
            )}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {editTask ? "Update your task information" : (
              <>
                {step === 1 && "Choose the category that best describes this task"}
                {step === 2 && "Provide the essential details for your task"}
                {step === 3 && "Break down your task into smaller checkpoints"}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {!editTask && step === 1 && (
            <CategorySelector
              selectedCategory={formData.category_type}
              onSelect={handleCategorySelect}
              taskCounts={taskCounts}
            />
          )}

          {(editTask || step === 2) && (
            <TaskDetailsForm
              formData={formData}
              onUpdate={handleDetailsUpdate}
            />
          )}

          {(editTask || step === 3) && (
            <CheckpointsEditor
              checkpoints={formData.checkpoints}
              onChange={(checkpoints) => setFormData({ ...formData, checkpoints })}
            />
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t gap-3">
          {!editTask && totalSteps > 0 && (
            <div className="text-sm text-muted-foreground order-2 sm:order-1">
              Step {step} of {totalSteps}
            </div>
          )}
          <div className={cn("flex gap-2 w-full sm:w-auto order-1 sm:order-2", editTask && "sm:ml-auto")}>
            {!editTask && step > 1 && (
              <Button variant="outline" onClick={handleBack} className="flex-1 sm:flex-none min-h-[44px]">
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
            {!editTask && step < 3 ? (
              <Button onClick={handleNext} disabled={!canProceed()} className="flex-1 sm:flex-none min-h-[44px]">
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSave} disabled={!canProceed()} className="flex-1 sm:flex-none min-h-[44px]">
                {editTask ? "Update Task" : "Create Task"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};